import { type Address, useGetAddress } from '@/api/address';
import { AutoComplete, type AutoCompleteOption } from '../Common/AutoComplete';
import { useState, useEffect, useRef, useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { formatCanadianPostalCode } from '@/utils/zipCode';
import { useTranslate } from '@/hooks/useTranslate';

interface Props {
  onSelect: (address: Address) => void;
}

type AddressOption = AutoCompleteOption & { addr: Address };

export const UnmanagedAddress = ({ onSelect }: Props) => {
  const {
    actions: { updateAddressDetails },
    customer,
  } = useContext(CheckoutContext);
  const [inputValue, setInputValue] = useState('');
  const [addressList, setAddressList] = useState<AddressOption[]>([]);
  const { mutateAsync: getAddress, isPending } = useGetAddress();
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useTranslate();
  const countryCode = customer?.taxCountry ?? customer?.geoLocator?.countryCode2;
  const isCanada = countryCode === 'CA';

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (!inputValue.trim()) {
      setAddressList([]);
      return;
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        const { addressResults } = await getAddress(inputValue);
        setAddressList(addressResults.map((addr) => ({ label: addr.address, value: addr.address, addr })));
      } catch (error) {
        console.error('Error fetching addresses:', error);
        setAddressList([]);
      }
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [inputValue, getAddress]);

  const handleSelect = (option: AutoCompleteOption) => {
    const selected = addressList.find((entry) => entry.value === option.value);
    if (!selected) return;
    onSelect(selected.addr);
    const formattedZipCode =
      selected.addr.zipCode && isCanada
        ? formatCanadianPostalCode(selected.addr.zipCode)
        : (selected.addr.zipCode ?? '');
    updateAddressDetails({ address: selected.addr.address, zipCode: formattedZipCode });
  };

  return (
    <div className="w-full">
      <AutoComplete
        onInputChange={setInputValue}
        options={addressList}
        onSelect={handleSelect}
        value={inputValue}
        placeholder={t('address')}
        className={'w-full'}
        error={''}
        disabled={false}
        isLoading={isPending}
        disableFiltering={true}
        forceSelection
      />
    </div>
  );
};
